<template>
  <v-app>
    <v-main>
      <v-container fluid>
        <div class="rec-board">
          <!-- Header curat de workspace -->
          <div class="rec-board__header">
            <div class="rec-board__header-left">
              <v-btn variant="text" size="small" class="rec-board__back" @click="goBack">
                <v-icon start>mdi-arrow-left</v-icon>{{ $t("recruitment.details.back") }}
              </v-btn>
              <v-icon class="rec-board__header-icon" size="18">mdi-account-search-outline</v-icon>
              <div class="rec-board__titles">
                <h1 class="rec-board__title">{{ $t("recruitment.details.title") }}</h1>
                <span v-if="requirement" class="rec-board__subtitle">{{ subtitle }}</span>
              </div>
            </div>
            <div class="rec-board__header-actions">
              <v-text-field
                v-model="search"
                :label="$t('recruitment.details.searchCandidate')"
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                class="rec-board__search"
              />
              <v-btn
                color="error"
                variant="flat"
                prepend-icon="mdi-email-remove-outline"
                :loading="sendingRejections"
                :disabled="sendingRejections || !hasRejectionTargets"
                @click="openRejectionDialog"
              >
                {{ $t("recruitment.details.sendRejection") }}
              </v-btn>
            </div>
          </div>

          <!-- Board pe etape (anatomie: NcrmLeadBoard) -->
          <div class="rec-board__columns">
            <div
              v-for="(status, colIndex) in orderedStatuses"
              :key="status"
              class="rec-board__column"
              :class="{ 'rec-board__column--drag-over': dragOverColumn === status }"
              @dragover.prevent="onColumnDragOver(status)"
              @dragleave="onColumnDragLeave(status)"
              @drop="onColumnDrop(status)"
            >
              <div class="rec-board__col-accent" :style="{ backgroundColor: accentColor(colIndex) }"></div>
              <div class="rec-board__col-header">
                <span class="rec-board__col-name">{{ statusLabel(status) }}</span>
                <v-chip size="x-small" :color="accentRole(colIndex)">
                  {{ (groupedAll[status] || []).length }}
                </v-chip>
              </div>

              <div class="rec-board__col-body">
                <div
                  v-for="item in groupedVisible[status] || []"
                  :key="item.id"
                  class="rec-board__card"
                  :class="{ 'rec-board__card--dragging': draggingItem && draggingItem.id === item.id }"
                  :draggable="true"
                  role="button"
                  tabindex="0"
                  :title="$t('recruitment.details.dragHint')"
                  @dragstart="onDragStart(item)"
                  @dragend="onDragEnd"
                  @click="goToCandidateDetails(item.id_candidates)"
                  @keydown.enter.prevent="goToCandidateDetails(item.id_candidates)"
                >
                  <div class="rec-board__card-head">
                    <span class="rec-board__card-title">{{ displayCandidateName(item.id_candidates) }}</span>
                    <v-chip size="x-small" class="flex-shrink-0">ID {{ item.id }}</v-chip>
                  </div>
                  <div v-if="displayCandidatePhone(item.id_candidates)" class="rec-board__card-meta">
                    <v-icon size="13" class="rec-board__card-meta-icon">mdi-phone-outline</v-icon>
                    <span>{{ displayCandidatePhone(item.id_candidates) }}</span>
                  </div>
                  <RecruitmentBoardCommentsPreview :comments="item.recent_comments" />
                  <div class="rec-board__card-footer">
                    <RecruitmentSubmissionStatusButton
                      v-if="requirement?.requirement_type === 'client'"
                      :application="item"
                      @open="openSubmission(item)"
                    />
                    <span class="rec-board__card-footer-spacer" />
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      :disabled="colIndex === 0"
                      :title="$t('recruitment.details.moveLeft')"
                      @click.stop="moveStage(item, -1)"
                    >
                      <v-icon size="16">mdi-chevron-left</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      :disabled="colIndex === orderedStatuses.length - 1"
                      :title="$t('recruitment.details.moveRight')"
                      @click.stop="moveStage(item, 1)"
                    >
                      <v-icon size="16">mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </div>

                <div
                  v-if="(groupedAll[status]?.length || 0) > visibleCountByStatus[status]"
                  class="rec-board__load-more"
                >
                  <v-btn size="small" variant="text" @click="loadMore(status)">
                    {{ $t("recruitment.details.loadMore") }}
                  </v-btn>
                </div>

                <div v-if="(groupedAll[status]?.length || 0) === 0" class="rec-board__empty">
                  <v-icon size="22" class="rec-board__empty-icon">mdi-inbox-outline</v-icon>
                  <span>{{ $t("recruitment.details.zeroState") }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Dialog confirmare respingere în masă -->
        <v-dialog v-model="rejectionDialog" max-width="460">
          <v-card>
            <v-card-title>{{ $t("recruitment.details.reject.title") }}</v-card-title>
            <v-card-text>{{ $t("recruitment.details.reject.body", { count: rejectionCount }) }}</v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" @click="rejectionDialog = false">{{ $t("app.actions.cancel") }}</v-btn>
              <v-btn color="error" variant="flat" :loading="sendingRejections" @click="confirmSendRejections">
                {{ $t("recruitment.details.reject.confirm") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <RecruitmentSubmissionDrawer
          v-model="submissionDrawerOpen"
          :application-id="selectedApplicationId"
          :read-only="!canSubmitCandidates"
          :initial-tab="submissionInitialTab"
          @sent="handleSubmissionSent"
        />

        <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" location="bottom right">
          {{ snackbarMessage }}
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";
import recruitmentSubmissionController from "@/Utile/recruitmentSubmissionController.mjs";
import RecruitmentBoardCommentsPreview from "@/components/recruitment/RecruitmentBoardCommentsPreview.vue";

const ACCENT_PALETTE = ["primary", "info", "success", "warning", "tertiary", "secondary"];

export default defineComponent({
  name: "RecrutareDetails",
  components: { RecruitmentBoardCommentsPreview },
  mixins: [recruitmentSubmissionController],
  props: { id: { type: [String, Number], required: true } },
  data() {
    return {
      requirement: null,
      applications: [],
      candidates: [],
      search: "",
      orderedStatuses: ["Sourcing", "LinkedIn", "Applied", "Screening", "Interview", "Offer", "Rejected", "Hired"],
      visibleCountByStatus: { Sourcing: 20, LinkedIn: 20, Applied: 20, Screening: 20, Interview: 20, Offer: 20, Rejected: 20, Hired: 20 },
      statusCanonical: { sourcing: "Sourcing", linkedin: "LinkedIn", applied: "Applied", screening: "Screening", interview: "Interview", offer: "Offer", rejected: "Rejected", hired: "Hired" },
      draggingItem: null,
      dragOverColumn: null,
      isSaving: false,
      sendingRejections: false,
      rejectionDialog: false,
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
    };
  },
  computed: {
    subtitle() {
      if (!this.requirement) return "";
      return [this.requirement.name_companies, this.requirement.name_vacancy]
        .filter(Boolean)
        .join(" — ");
    },
    candidatesById() {
      const map = {};
      for (const c of this.candidates) {
        map[c.id] = c;
      }
      return map;
    },
    enriched() {
      return this.applications.map((a) => {
        const cand = this.candidatesById[a.id_candidates] || {};
        return {
          ...a,
          _status: this.normalizeStatus(a.status),
          _candidateName: this.makeCandidateName(cand),
        };
      });
    },
    filtered() {
      if (!this.search) return this.enriched;
      const term = this.search.toLowerCase();
      return this.enriched.filter((r) =>
        String(r._candidateName || "").toLowerCase().includes(term)
      );
    },
    groupedAll() {
      const groups = {};
      for (const s of this.orderedStatuses) groups[s] = [];
      for (const row of this.filtered) {
        const key = row._status || "Applied";
        if (!groups[key]) groups[key] = [];
        groups[key].push(row);
      }
      for (const s of Object.keys(groups)) {
        groups[s].sort((a, b) => (b.id || 0) - (a.id || 0));
      }
      return groups;
    },
    groupedVisible() {
      const out = {};
      for (const s of this.orderedStatuses) {
        out[s] = (this.groupedAll[s] || []).slice(0, this.visibleCountByStatus[s]);
      }
      return out;
    },
    hasRejectionTargets() {
      return this.rejectionCount > 0;
    },
    rejectionCount() {
      return this.applications.filter(
        (app) => this.normalizeStatus(app.status) !== "Hired"
      ).length;
    },
  },
  created() {
    this.fetchRequirement();
    this.fetchCandidates();
    this.fetchApplications();
  },
  watch: {
    id() {
      this.fetchRequirement();
      this.fetchApplications();
    },
  },
  methods: {
    accentRole(index) {
      return ACCENT_PALETTE[index % ACCENT_PALETTE.length];
    },
    accentColor(index) {
      return `rgb(var(--v-theme-${this.accentRole(index)}))`;
    },
    statusLabel(status) {
      return this.$t(`recruitment.status.${status}`) || status;
    },
    notify(message, color = "success") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    onRecruitmentSubmissionSent() {
      this.notify(this.$t("recruitment.submission.completed"), "success");
      this.fetchApplications();
    },
    openRejectionDialog() {
      if (!this.rejectionCount) {
        this.notify(this.$t("recruitment.details.reject.noTargets"), "warning");
        return;
      }
      this.rejectionDialog = true;
    },
    confirmSendRejections() {
      if (this.sendingRejections) return;
      this.sendingRejections = true;

      axios
        .post(`/candidati/recruitment_requirements/${this.id}/send-rejections`)
        .then((res) => {
          const data = res?.data || {};
          const sent = Array.isArray(data.sent) ? data.sent.length : 0;
          const skipped = Array.isArray(data.skipped) ? data.skipped.length : 0;
          const errors = Array.isArray(data.errors) ? data.errors.length : 0;
          this.notify(
            this.$t("recruitment.details.reject.result", { sent, skipped, errors }),
            errors ? "warning" : "success"
          );
        })
        .catch((error) => {
          console.error("Eroare la trimiterea mesajelor de respingere:", error);
          const msg =
            error?.response?.data?.error || this.$t("recruitment.details.reject.error");
          this.notify(msg, "error");
        })
        .finally(() => {
          this.sendingRejections = false;
          this.rejectionDialog = false;
          this.fetchApplications();
          this.fetchCandidates();
        });
    },
    goBack() {
      if (window.history.length > 1) {
        try { this.$router.back(); } catch { this.$router.push({ name: "Recrutare" }); }
      } else {
        this.$router.push({ name: "Recrutare" });
      }
    },
    onDragStart(item) {
      this.draggingItem = item;
    },
    onDragEnd() {
      this.draggingItem = null;
      this.dragOverColumn = null;
    },
    onColumnDragOver(status) {
      if (this.draggingItem) this.dragOverColumn = status;
    },
    onColumnDragLeave(status) {
      if (this.dragOverColumn === status) this.dragOverColumn = null;
    },
    onColumnDrop(targetStatus) {
      if (!this.draggingItem) return;
      const item = this.draggingItem;
      this.dragOverColumn = null;
      this.applyStatusChange(item, targetStatus);
      this.onDragEnd();
    },
    // Mută aplicația la etapa vecină (chevron). Operabil din click/tastatură — nu doar drag.
    moveStage(item, direction) {
      const current = this.normalizeStatus(item.status);
      const i = this.orderedStatuses.indexOf(current);
      if (i === -1) return;
      const target = this.orderedStatuses[i + direction];
      if (!target) return;
      this.applyStatusChange(item, target);
    },
    applyStatusChange(item, targetStatus) {
      const currentStatus = this.normalizeStatus(item.status);
      if (currentStatus === targetStatus) return;

      const idx = this.applications.findIndex((a) => a.id === item.id);
      if (idx === -1) return;

      // Optimistic UI update
      const prev = { ...this.applications[idx] };
      this.applications[idx] = { ...prev, status: targetStatus };
      this.isSaving = true;

      const payload = {
        id_candidates: item.id_candidates,
        id_recruitment_requirements: item.id_recruitment_requirements,
        status: targetStatus,
      };

      axios
        .put(`/candidati/candidate_applications/${item.id}`, payload)
        .then(() => {
          this.isSaving = false;
        })
        .catch((err) => {
          console.error("Eroare la actualizarea statusului:", err);
          this.applications[idx] = prev; // revert
          this.notify(this.$t("recruitment.details.statusError"), "error");
        });
    },
    normalizeStatus(s) {
      if (!s) return "Applied";
      const key = String(s).trim().toLowerCase();
      return this.statusCanonical[key] || s;
    },
    makeCandidateName(c) {
      if (!c) return "";
      const first = c.FirstName || "";
      const last = c.LastName || "";
      const name = `${first} ${last}`.trim();
      return name || (c.Email ? String(c.Email) : "");
    },
    displayCandidateName(id) {
      const c = this.candidatesById[id];
      return this.makeCandidateName(c) || `Candidat #${id}`;
    },
    displayCandidatePhone(id) {
      const c = this.candidatesById[id];
      return c && (c.PhoneNumber || c.phone || c.phone_number)
        ? c.PhoneNumber || c.phone || c.phone_number
        : "";
    },
    loadMore(status) {
      this.visibleCountByStatus[status] += 20;
    },
    goToCandidateDetails(id) {
      if (!id) return;
      try {
        this.$router.push({ name: "CandidateDetails", params: { id } });
      } catch {
        // dacă ruta nu există, ignorăm
      }
    },
    fetchRequirement() {
      axios
        .get(`/candidati/recruitment_requirements/${this.id}`)
        .then((res) => { this.requirement = res.data || null; })
        .catch(() => { this.requirement = null; });
    },
    fetchCandidates() {
      axios
        .get("/candidati/candidats")
        .then((res) => { this.candidates = res.data || []; })
        .catch(() => { this.candidates = []; });
    },
    fetchApplications() {
      axios
        .get("/candidati/candidate_applications", {
          params: { id_recruitment_requirements: this.id, include_recent_comments: true },
        })
        .then((res) => { this.applications = res.data || []; })
        .catch(() => { this.applications = []; });
    },
  },
});
</script>

<style scoped src="./RecrutareDetails.css"></style>
